import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { estimateCourierShipping } from './tb-b2b-shipping-estimate.js';

class BulkEconomics extends Component {
  #configuration;
  #selected;
  #controller;
  #selectionGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#configuration = JSON.parse(this.refs.configuration.textContent);
    for (const key of ['shippingRates', 'shippingFx']) {
      try {
        this.#configuration[key] = JSON.parse(this.#configuration[key]);
      } catch {
        this.#configuration[key] = {};
      }
    }
    this.#selected = this.#configuration.variants.find(v => String(v.id) === String(this.#configuration.selectedId));
    const signal = this.#controller.signal;
    document.addEventListener(StandardEvents.productSelect, this.#onSelect, {signal});
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, {signal});
    this.updateEstimate();
  }

  updatedCallback() {
    super.updatedCallback();
    this.updateEstimate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #scope() {
    return this.closest('.shopify-section');
  }

  #quantitySelector() {
    return this.#scope()?.querySelector('product-form-component[data-product-id="' + this.dataset.productId + '"] quantity-selector-component');
  }

  #onQuantity = event => {
    if (!(event.target instanceof Element) || event.target.closest('.shopify-section') !== this.#scope()) return;
    if (event.detail?.cartLine) return;
    this.updateEstimate();
  };

  #onSelect = async event => {
    if (!(event.target instanceof Element) || event.target.closest('.shopify-section') !== this.#scope()) return;
    if (event.target.closest('product-card, quick-add-dialog') || !event.promise) return;
    const generation = ++this.#selectionGeneration;
    try {
      const {detail} = await event.promise;
      if (!this.isConnected || generation !== this.#selectionGeneration) return;
      const selected = this.#configuration.variants.find(v => String(v.id) === String(detail?.resource?.id));
      if (!selected) return;
      this.#selected = selected;
      queueMicrotask(() => this.updateEstimate());
    } catch {
      if (this.isConnected) this.refs.shippingMessage.textContent = this.#configuration.strings.refresh;
    }
  };

  #money(cents, precision = 2) {
    return new Intl.NumberFormat(this.#configuration.locale, {
      style:'currency', currency:this.#configuration.currency, currencyDisplay:'code',
      minimumFractionDigits:precision, maximumFractionDigits:precision
    }).format(cents / 100);
  }

  #fill(template, variables) {
    for (const [key, value] of Object.entries(variables)) template = template.replaceAll('[' + key + ']', value);
    return template;
  }

  #range(low, high, precision = 2) {
    if (low === high) return this.#money(low, precision);
    const upper = new Intl.NumberFormat(this.#configuration.locale, {
      minimumFractionDigits: precision, maximumFractionDigits: precision
    }).format(high / 100);
    return this.#money(low, precision) + '–' + upper;
  }

  #optionalCost(input) {
    if (!input.value.trim() || !input.validity.valid) return null;
    const amount = Number(input.value);
    return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : null;
  }

  updateEstimate() {
    const variant = this.#selected;
    if (!variant || !(variant.grams > 0)) return;
    const config = this.#configuration;
    const text = config.strings;
    const quantity = Math.max(1, Number(this.#quantitySelector()?.getValue?.()) || 1);
    const grams = variant.grams * quantity;
    const ingredient = variant.price * quantity;
    const country = this.refs.destination.value;
    const shipping = estimateCourierShipping({grams, country, currency:config.currency, rates:config.shippingRates, fx:config.shippingFx});
    const tax = this.#optionalCost(this.refs.taxInput);
    const other = this.#optionalCost(this.refs.otherInput);
    const base = ingredient + (tax ?? 0) + (other ?? 0);
    const totalLow = base + (shipping?.low ?? 0);
    const totalHigh = base + (shipping?.high ?? 0);
    this.refs.selection.textContent = quantity + ' × ' + variant.title;
    this.refs.unitLabel.textContent = shipping === null ? text.beforeShipping : text.estimated;
    this.querySelector('.tbp-economics__preview > span').textContent = shipping === null ? text.beforeShipping : text.deliveredLabel;
    this.refs.unitCost.textContent = this.#range(totalLow * 1000 / grams, totalHigh * 1000 / grams, 2) + ' ' + text.perGram;
    this.refs.ingredientTotal.textContent = this.#money(ingredient);
    this.refs.shippingTotal.textContent = shipping === null ? text.checkout : this.#range(shipping.low, shipping.high);
    this.refs.shippingBasis.textContent = shipping === null ? text.quote : this.#fill(text.shippingBand, {weight:shipping.band / 1000,low:shipping.inrLow,high:shipping.inrHigh});
    this.refs.taxTotal.textContent = tax !== null ? this.#money(tax) : config.taxesIncluded ? text.included : text.checkout;
    this.refs.otherTotal.textContent = other !== null ? this.#money(other) : text.notEntered;
    this.refs.planningTotal.textContent = this.#range(totalLow, totalHigh);
    const progress = Math.min(100, grams / 5000 * 100);
    this.refs.shippingProgress.hidden = false;
    this.refs.shippingProgress.value = progress;
    this.refs.shippingProgress.setAttribute('aria-valuetext', Math.round(progress) + '%');
    this.refs.shippingMessage.textContent = grams < 5000 ? this.#fill(text.remainingWeight, {weight:(5000 - grams) / 1000}) : text.bulkWeightReached;
    this.refs.shippingGoal.textContent = this.#fill(text.weightGoal, {weight:grams / 1000});
    const bulk = config.variants.find(v => v.grams === 5000 && v.available);
    const reference = config.variants.find(v => v.grams === 1000);
    const percent = bulk ? Math.max(0, (1 - (bulk.price / bulk.grams) / (variant.price / variant.grams)) * 100) : 0;
    const bulkSaving = bulk && reference ? Math.max(0, Math.round(reference.price * 5 - bulk.price)) : 0;
    this.refs.bulkButton.hidden = !(bulk && variant.id !== bulk.id && percent >= 0.1);
    this.refs.bulkButton.textContent = this.#fill(text.bulkOffer, {percent:Math.round(percent)});
    this.refs.bulkDetail.hidden = !bulkSaving;
    this.refs.bulkDetail.textContent = variant.grams === 5000
      ? text.bulkSelected
      : this.#fill(text.bulkSaving, {amount:this.#money(bulkSaving)});
    this.querySelectorAll('[data-economic-variant]').forEach(row => row.classList.toggle('tbp-pack-row--selected', row.dataset.economicVariant === String(variant.id)));
  }

  #selectPack(id) {
    const picker = this.#scope()?.querySelector('variant-picker');
    const choice = picker?.querySelector('[data-variant-id="' + id + '"]');
    if (choice instanceof HTMLInputElement && !choice.disabled) choice.click();
    if (choice instanceof HTMLOptionElement && !choice.disabled) {
      const select = choice.closest('select');
      if (select) {
        select.value = choice.value;
        select.dispatchEvent(new Event('change', {bubbles:true}));
      }
    }
  }

  chooseBulk() {
    const bulk = this.#configuration.variants.find(v => v.grams === 5000 && v.available);
    if (!bulk) return;
    const selector = this.#quantitySelector();
    selector?.setValue?.('1');
    selector?.onQuantityChange?.();
    this.#selectPack(bulk.id);
  }

  choosePack(event) {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-economic-pack]');
    if (!link) return;
    event.preventDefault();
    this.#selectPack(link.dataset.economicPack);
  }
}

if (!customElements.get('tb-bulk-economics')) customElements.define('tb-bulk-economics', BulkEconomics);

